import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { environment } from '../../../environments/environment';

export type StreamStatus = 'stop' | 'stopping' | 'recording';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'custom-stream-deepgram',
  template: ''
})
export class CustomStreamDeepgramComponent implements OnInit {
  @Input()
  public language: any;
  @Input()
  public audioDeviceId: string;
  @Input()
  public email: string;
  @Output()
  public transcriptionHandler: EventEmitter<string>;
  @Output()
  public statusHandler: EventEmitter<StreamStatus>;
  public transcription: string;
  public status: StreamStatus;
  private socket: WebSocket;
  private microphone: MediaRecorder;
  private sendingChunks: boolean;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.transcriptionHandler = new EventEmitter();
    this.statusHandler = new EventEmitter();
  }

  ngOnInit() {
    this.initVariables();
  }

  private initVariables = (): void => {
    this.clearTranscription();
  };

  public clearTranscription = (): void => {
    this.changeStreamStatus('stop');
    this.transcription = '';
    this.sendingChunks = false;
  };

  private changeStreamStatus = (status: StreamStatus): void => {
    if (status === this.status) {
      return;
    }
    this.status = status;
    this.statusHandler.emit(status);
    this.changeDetectorRef.detectChanges();
  };

  private initStreamRecording = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      this.socket = new WebSocket(`wss://${environment.streamingApi}?language=${this.language}&email=${this.email}`);

      this.socket.addEventListener('open', async () => {
        console.log('client: connected to server');
        resolve();
      });

      this.socket.addEventListener('error', (error) => {
        console.error('WebSocket error:', error);
        reject(error);
      });

      this.socket.addEventListener('message', (event) => {
        this.validateAndStopRecording();
        if (event.data === '') {
          return;
        }
        try {
          const data = JSON.parse(event.data);
          if (data && data.channel && data.channel.alternatives[0].transcript === '') {
            this.transcriptionHandler.emit(this.transcription);
          } else if (data && data.channel && data.channel.alternatives[0].transcript !== '') {
            this.transcription += ' ' + data.channel.alternatives[0].transcript;
            this.transcriptionHandler.emit(this.transcription);
          }
        } catch (e) {
          console.error('Failed to parse JSON:', e);
          return;
        }
      });

      this.socket.addEventListener('close', () => {
        console.log('client: disconnected from server');
      });
    });
  };

  public startRecording = async (): Promise<void> => {
    this.changeStreamStatus('recording');
    await this.initStreamRecording();
    this.microphone = await this.getMicrophone();
    await this.openMicrophone(this.microphone, this.socket);
  };

  public stopRecording = (): void => {
    this.changeStreamStatus('stopping');
  };

  public destroyStreamingRecording = (): void => {
    this.changeStreamStatus('stop');
    this.transcription = '';
    this.socket?.close(1000, 'stopRecording');
    this.microphone?.stop();
    this.microphone?.stream?.getTracks()?.forEach((track: any) => {
      track.stop();
    });
  };

  private validateAndStopRecording = (): void => {
    if (this.status === 'stopping' && this.sendingChunks) {
      this.changeStreamStatus('stop');
      this.sendingChunks = false;
      this.socket?.close(1000, 'stopRecording');
      this.microphone.stop();
      this.microphone.stream.getTracks()?.forEach((track: any) => {
        track.stop();
      });
    }
  };

  private getMicrophone = async (): Promise<MediaRecorder> => {
    try {
      const audioConstraints: MediaTrackConstraints = {
        deviceId: this.audioDeviceId ? { exact: this.audioDeviceId } : undefined,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        sampleRate: 16000
      };
      const stream = await navigator.mediaDevices.getUserMedia({audio: audioConstraints});
      return new MediaRecorder(stream, {mimeType: 'audio/webm'});
    } catch (error) {
      console.error('error accessing microphone:', error);
      throw error;
    }
  };

  private openMicrophone = async (microphone: MediaRecorder, socket: WebSocket): Promise<void> => new Promise((resolve) => {
    microphone.onstart = () => {
      resolve(undefined);
    };

    microphone.onstop = () => {
    };

    microphone.ondataavailable = (event) => {
      console.log('Data available - size:', event.data.size, 'socket ready:', socket.readyState === WebSocket.OPEN);
      if (event.data.size > 0 && socket.readyState === WebSocket.OPEN) {
        this.sendingChunks = true;
        socket.send(event.data);
      } else {
        console.warn('❌ Cannot send - size:', event.data.size, 'socket state:', socket.readyState);
      }
    };

    microphone.start(1000);
  });

}
